import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store/store';
import { Layout } from '../layout';
import { PendingApproval } from '../screens/PendingApproval';
import OrganizationLobby from '../screens/OrganizationLobby';
import AccountPendingDeletion from '../screens/AccountPendingDeletion';
import UpdateRequired from '../screens/UpdateRequired';
import {
  useGetAppConfigQuery,
  useGetSubscriptionsByOrganizationQuery,
  useGetUserQuery,
} from '../redux/api';
import { currentUserId } from '../utils/auth';

// package.json version with the dots stripped, matching the mobile versionCode
// convention documented in documentations/release-notes.md (1.0.016 -> 10016).
// Relies on the three-digit patch padding that convention uses; a bare "1.0.16"
// would collapse to 1016 and compare wrongly.
const APP_VERSION_CODE = Number(__APP_VERSION__.replace(/\./g, ''));

// Authenticated experience. Org users first pick an organization (the lobby),
// then only reach the full application shell once that organization has an
// ACTIVE subscription; until then they see a pending-approval screen. Platform
// owner and broker skip both the lobby and the subscription gate.
const AuthScreens: React.FC = () => {
  const user = useSelector((s: RootState) => s.layout.user);
  const orgSelected = useSelector((s: RootState) => s.layout.orgSelected);
  const role = user?.role;
  const orgId = user?.organizationId;

  const bypass = role === 'platform owner' || role === 'broker' || !orgId;

  const myId = currentUserId();
  const { data: me } = useGetUserQuery(myId ?? '', { skip: !myId });

  // Until the query resolves the floor reads as 0, so a slow or failed request
  // never locks anyone out of a perfectly good bundle.
  const { data: appConfig } = useGetAppConfigQuery();
  // The platform owner is exempt, deliberately. This gate is a kill switch set
  // from a screen INSIDE the app shell (Settings -> App Configuration), so
  // gating the only role that can lower the floor makes a mistyped value
  // unrecoverable from the UI: the operator gets the same "Update required"
  // wall as everyone else, and the sole way back is a hand-written API or
  // database call. An owner on a stale bundle is a far smaller problem than an
  // owner who cannot reach the control that unbricks everyone else.
  const staleBundle =
    role !== 'platform owner' &&
    Number.isFinite(APP_VERSION_CODE) &&
    APP_VERSION_CODE < (appConfig?.webappMinVersionCode ?? 0);

  const {
    data: subscriptions = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetSubscriptionsByOrganizationQuery(orgId ?? '', {
    skip: bypass || !orgSelected,
  });

  // Ahead of every other gate: on a bundle this old, whatever those screens
  // would render can't be trusted anyway.
  if (staleBundle) return <UpdateRequired message={appConfig?.updateMessage} />;

  // A self-service deletion request takes priority over everything else —
  // the account is still logged-in-able (that's how restore works) but
  // shouldn't reach the org lobby/subscription gate/app shell until restored.
  if (me?.pendingDeletion) return <AccountPendingDeletion pendingDeletion={me.pendingDeletion} />;

  // Org users choose an organization before anything else.
  if (!orgSelected) return <OrganizationLobby />;

  if (bypass) return <Layout />;

  if (isLoading) {
    return (
      <div className="login">
        <div className="login__card">
          <p className="login__sub">Loading your workspace…</p>
        </div>
      </div>
    );
  }

  const hasActive = subscriptions.some(
    (s) => (s?.status ?? '').toLowerCase() === 'active',
  );
  // Block only when a subscription exists but none is active yet.
  const blocked = subscriptions.length > 0 && !hasActive;

  if (blocked) {
    return <PendingApproval onRefresh={() => refetch()} refreshing={isFetching} />;
  }

  return <Layout />;
};

export default AuthScreens;
